/******/ (() => {
  class CartQuantity extends HTMLElement {
    constructor() {
      super();
      this.removeButton = this.querySelector('[data-cart-remove-item]');
      this.adjustButtons = this.querySelectorAll('[data-cart-quantity-button]');
      this.input = this.querySelector('[data-cart-quantity-input]');
      this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
      this.handleButtonClick = this.handleButtonClick.bind(this);
      this.handleInputChange = this.handleInputChange.bind(this);
      this.handleQuantityChange = this.handleQuantityChange.bind(this);
      this.handleRemoveItem = this.handleRemoveItem.bind(this);
      this.updateCart = this.updateCart.bind(this);
      this.updateLineItem = this.updateLineItem.bind(this);
      this.updateCartCountIndicator = this.updateCartCountIndicator.bind(this);
      this.handleCartErrors = this.handleCartErrors.bind(this);
      this.setLoadingState = this.setLoadingState.bind(this);
      this.hideLoadingState = this.hideLoadingState.bind(this);
      this.setRemoveLoadingState = this.setRemoveLoadingState.bind(this);
      this.hideRemoveLoadingState = this.hideRemoveLoadingState.bind(this);
    }

    connectedCallback() {
      window.wetheme.webcomponentRegistry.register({ key: 'component-cart-quantity' });
      this.removeButton?.addEventListener('click', this.handleRemoveItem);
      this.adjustButtons.forEach(button => button.addEventListener('click', this.handleButtonClick));
      this.input?.addEventListener('change', this.handleInputChange);
    }

    get sectionsRequestParam() {
      const extra = (this.getAttribute('data-cart-section-ids') || '').trim();
      if (!extra) {
        return 'cart-drawer';
      }
      const ids = extra.split(',').map(s => s.trim()).filter(Boolean);
      if (!ids.includes('cart-drawer')) {
        ids.push('cart-drawer');
      }
      return [...new Set(ids)].join(',');
    }

    applyCartPageSectionUpdates(parsedResponse) {
      if (!parsedResponse.sections) {
        return;
      }
      for (const [sectionId, html] of Object.entries(parsedResponse.sections)) {
        if (sectionId === 'cart-drawer' || !html) {
          continue;
        }
        const current = document.getElementById(`shopify-section-${sectionId}`);
        if (!current) {
          continue;
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById(`shopify-section-${sectionId}`);
        if (fresh) {
          current.replaceWith(fresh);
        }
      }
      document.dispatchEvent(new CustomEvent('wetheme:cart-main-section:updated', { bubbles: true }));
      if (typeof window.wetheme?.showOnScroll === 'function') {
        window.wetheme.showOnScroll();
      }
    }

    handleButtonClick(e) {
      e.preventDefault();
      const previousValue = this.input.value;
      const adjustment = e.currentTarget.name === 'plus' ? 1 : -1;
      this.adjustQuantity(adjustment);
      if (previousValue !== this.input.value) {
        this.handleQuantityChange(this.input.value);
      }
    }

    adjustQuantity(adjustment) {
      const newValue = parseInt(this.input.value, 10) + adjustment;
      this.input.value = isNaN(newValue) ? 1 : newValue;
      if (this.input.min && parseInt(this.input.value, 10) < parseInt(this.input.min, 10)) {
        this.input.value = this.input.min;
      }
    }

    handleInputChange(e) {
      e.preventDefault();
      if (isNaN(this.input.value)) {
        this.input.value = 1;
      }
      if (this.input.min && parseInt(this.input.value, 10) < parseInt(this.input.min, 10)) {
        this.input.value = this.input.min;
      }
      this.handleQuantityChange(this.input.value);
    }

    handleQuantityChange(inputValue) {
      const quantity = parseInt(inputValue, 10);
      this.updateCart(this.itemIndex, quantity);
    }

    handleRemoveItem() {
      this.updateCart(this.itemIndex, 0);
    }

    async updateCart(line, quantity) {
      try {
        this.setLoadingState();
        if (quantity === 0) {
          this.setRemoveLoadingState();
        }
        let response = await this.updateLineItem(line, quantity);
        if (!response) {
          this.hideLoadingState();
          this.hideRemoveLoadingState();
          return;
        }
        if (this.hasGiftWrap) {
          const adjustment = quantity - this.previousQuantity;
          const cart = await this.getCart();
          if (this.getGiftWrapLine(cart) && this.getGiftWrapQuantity(cart)) {
            response = await this.updateLineItem(this.getGiftWrapLine(cart), this.getGiftWrapQuantity(cart) + adjustment);
          }
        }
        if (!response) {
          this.hideLoadingState();
          this.hideRemoveLoadingState();
          return;
        }
        this.applyCartPageSectionUpdates(response);
        window.eventBus.emit('update:cart:drawer', response);
        this.updateCartCountIndicator();
        this.hideLoadingState();
        this.hideRemoveLoadingState();
      } catch (error) {
        console.error('Error updating cart:', error);
        this.hideLoadingState();
        this.hideRemoveLoadingState();
      }
    }

    async updateLineItem(line, quantity) {
      try {
        const body = JSON.stringify({
          line,
          quantity,
          sections: this.sectionsRequestParam,
        });
        const response = await fetch(window.routes.cart_change_url, {
          ...this.fetchConfig(),
          body,
        });
        const responseText = await response.text();
        if (!responseText) {
          this.handleCartErrors();
          return null;
        }
        const parsedResponse = JSON.parse(responseText);
        if (parsedResponse.errors) {
          this.handleCartErrors(line, parsedResponse.errors);
          return null;
        }
        return parsedResponse;
      } catch (error) {
        console.error('Error updating cart line item:', error);
        return null;
      }
    }

    fetchConfig(type = 'json') {
      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: `application/${type}`,
        },
      };
    }

    handleCartErrors(line, errors) {
      this.hideLoadingState();
      this.hideRemoveLoadingState();
      this.input.value = this.input.getAttribute('value');
      this.renderErrorMessage(line, errors);
    }

    renderErrorMessage(line, message) {
      const errorElement = document.querySelector(`[data-cart-error-index="${line}"]`);
      if (!errorElement) {
        return;
      }
      errorElement.innerHTML = message;
      errorElement.setAttribute('aria-hidden', false);
      const isLastItem = errorElement.getAttribute('data-cart-is-last-item') === 'true';
      if (isLastItem) {
        errorElement.scrollIntoView({ behavior: 'smooth' });
      }
    }

    async getCart() {
      try {
        const response = await fetch(`${window.routes.cart_url}?view=compare`);
        return await response.json();
      } catch (error) {
        console.error(`Error fetching cart: ${error.description || error.message}`);
      }
    }

    updateCartCountIndicator() {
      if (!this.cartCountIndicator) {
        return;
      }
      this.getCart().then(cart => window.wetheme.updateCartCount(cart));
    }

    getGiftWrapLine(cart) {
      return cart.items.findIndex(item => item.variant_id === this.giftWrapVariantId) + 1 || null;
    }

    getGiftWrapQuantity(cart) {
      return cart.items.find(item => item.variant_id === this.giftWrapVariantId)?.quantity || null;
    }

    setLoadingState() {
      this.classList.add('is-loading');
    }

    hideLoadingState() {
      this.classList.remove('is-loading');
    }

    setRemoveLoadingState() {
      this.classList.add('is-removing');
    }

    hideRemoveLoadingState() {
      this.classList.remove('is-removing');
    }

    get itemIndex() {
      return parseInt(this.getAttribute('data-item-index'), 10);
    }

    get previousQuantity() {
      return parseInt(this.getAttribute('data-item-quantity'), 10);
    }

    get hasGiftWrap() {
      return this.getAttribute('data-has-gift-wrap') === 'true';
    }

    get giftWrapVariantId() {
      return parseInt(this.getAttribute('data-gift-wrap-variant-id'), 10);
    }
  }

  customElements.define('cart-quantity', CartQuantity);
})();
